import { PageSize } from '@/constant';
import {
	TGetListSystemUnitParams,
	TPayloadUpdateSuccess,
	TSystemUnitItem,
} from '@/services';
import { mergeItemUpdated, mergeListItemUpdated } from '@/utils';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { RootState } from '..';
import { formatSystemUnitDetail, formatSystemUnitList } from './unit.helper';
import {
	ICreateUnitRequest,
	IPayloadDeleteUnitRequest,
	IPayloadGetUnitDetailRequest,
	IPayloadGetUnitDetailSuccess,
	IPayloadGetUnitListRequest,
	IPayloadGetUnitListSuccess,
	IUnitDetailStore,
	TPayloadGetHistoryUpdateUnitListRequest,
	TPayloadGetSystemHistoryUnitListSuccess,
	TSystemUnitHistoryUpdateDetailStore,
} from './unit.type';

const InitParams: TGetListSystemUnitParams = {
	page: 1,
	pageSize: PageSize.S,
	searchTypes: [],
};

const initialState = {
	fetching: false,
	unitList: [] as IUnitDetailStore[],
	total: 0,
	params: InitParams,
	unitDetail: {} as IUnitDetailStore,
	historyUpdateUnitList: [] as TSystemUnitHistoryUpdateDetailStore[],
	historyParams: {
		page: 1,
		pageSize: PageSize.XXS,
		field: '',
		total: 0,
	},
};

const SystemUnitSlice = createSlice({
	initialState,
	name: 'systemUnit',
	reducers: {
		/**---- GET COMPANY_MANAGEMENT LIST START ---- */
		getSystemUnitListRequest: (
			state,
			action: PayloadAction<IPayloadGetUnitListRequest>
		) => {
			return {
				...state,
				fetching: true,
				params: action?.payload,
			};
		},
		getSystemUnitListSuccess: (
			state,
			action: PayloadAction<IPayloadGetUnitListSuccess>
		) => {
			const { unitList, total } = action.payload;
			state.fetching = false;
			state.unitList = unitList;
			state.total = total;
		},
		getSystemUnitListFailure: (state) => {
			state.fetching = false;
			state.total = 0;
			state.unitList = [];
		},
		/**---- GET COMPANY_MANAGEMENT LIST END ---- */

		/**---- CREATE COMPANY_MANAGEMENT START ---- */
		createSystemUnitRequest: (
			state,
			action: PayloadAction<ICreateUnitRequest>
		) => {
			state.fetching = true;
		},
		createSystemUnitSuccess: (state) => {
			state.fetching = false;
		},
		createSystemUnitFailure: (state) => {
			state.fetching = false;
		},
		/**---- CREATE COMPANY_MANAGEMENT END ---- */

		/**---- GET COMPANY_MANAGEMENT DETAIL START ---- */
		getSystemUnitDetailRequest: (
			state,
			action: PayloadAction<IPayloadGetUnitDetailRequest>
		) => {
			state.fetching = true;
		},
		getSystemUnitDetailSuccess: (
			state,
			action: PayloadAction<IPayloadGetUnitDetailSuccess>
		) => {
			const { unitDetail } = action.payload;
			state.fetching = false;
			state.unitDetail = unitDetail;
		},
		getSystemUnitDetailFailure: (state) => {
			state.fetching = false;
		},
		/**---- GET COMPANY_MANAGEMENT DETAIL END ---- */

		/**---- DELETE COMPANY_MANAGEMENT START ---- */
		deleteSystemUnitRequest: (
			state,
			action: PayloadAction<IPayloadDeleteUnitRequest>
		) => {
			state.fetching = true;
		},
		deleteSystemUnitSuccess: (state) => {
			state.fetching = false;
		},
		deleteSystemUnitFailure: (state) => {
			state.fetching = false;
		},
		/**---- DELETE COMPANY_MANAGEMENT END ---- */

		/**---- HISTORY UPDATE UNIT START ---- */
		getSystemHistoryUpdateSystemUnitListRequest: (
			state,
			action: PayloadAction<TPayloadGetHistoryUpdateUnitListRequest>
		) => {
			const { field, page, pageSize } = action.payload;
			state.fetching = true;
			state.historyParams = {
				page,
				field,
				pageSize,
				total: 0,
			};
		},
		getSystemHistoryUpdateSystemUnitListSuccess: (
			state,
			action: PayloadAction<TPayloadGetSystemHistoryUnitListSuccess>
		) => {
			const { total, historyUpdateUnitList } = action.payload;
			state.fetching = false;
			state.historyUpdateUnitList = historyUpdateUnitList;
			state.historyParams.total = total;
		},
		getSystemHistoryUpdateSystemUnitListFailure: (state) => {
			state.fetching = false;
		},

		/**---- UPDATE COMPANY_MANAGEMENT DETAIL START ---- */
		updateSystemUnitDetailSuccess: (
			state,
			action: PayloadAction<TPayloadUpdateSuccess<TSystemUnitItem>>
		) => {
			const { data } = action.payload;
			const unitDetail = { ...state.unitDetail };
			state.unitDetail = formatSystemUnitDetail(
				mergeItemUpdated<IUnitDetailStore, TSystemUnitItem>(unitDetail, data)
			);
		},
		/**---- UPDATE COMPANY_MANAGEMENT DETAIL END ---- */
		updateSystemUnitSuccess: (
			state,
			action: PayloadAction<TPayloadUpdateSuccess<TSystemUnitItem>>
		) => {
			const { data } = action.payload;
			const unitList = [...state.unitList];
			state.unitList = formatSystemUnitList(
				mergeListItemUpdated<IUnitDetailStore, TSystemUnitItem>(unitList, data)
			);
		},
	},
});

export const SystemUnitSelectors = {
	selectFetching: (state: RootState) => state.systemUnit.fetching,
	selectParams: (state: RootState) => state.systemUnit.params,
	selectTotal: (state: RootState) => state.systemUnit.total,
	selectUnitList: (state: RootState) => state.systemUnit.unitList,
	selectUnitDetail: (state: RootState) => state.systemUnit.unitDetail,
	selectHistoryUpdateUnitList: (state: RootState) =>
		state.systemUnit.historyUpdateUnitList,
	selectHistoryParams: (state: RootState) => state.systemUnit.historyParams,
};

export const SystemUnitActions = SystemUnitSlice.actions;
export const SystemUnitReducer = SystemUnitSlice.reducer;
